import React from "react";
import {useTranslation} from "react-i18next";
import {B2BAgencyStatsResponse, ClientTripbook} from "../../domain/entities/B2BAgencyStats.ts";
import {B2BReferralStatsResponse} from "../../domain/entities/B2BReferralStats.ts";
import {useAgencyTripbooksFeed} from "../../hooks/useAgencyTripbooksFeed.ts";
import {useB2BReferralStats} from "../../hooks/useB2BReferralStats.ts";
import {useAgencyPackActivity} from "../../hooks/useAgencyPackActivity.ts";
import {AgencyFeedbackEntry, usePackagedAgencyFeedback, usePayPerUseAgencyFeedback} from "../../hooks/useAgencyClientFeedback.ts";
import {Button} from "../Button.tsx";
import IconBook from '../../assets/icons/book.svg?react';
import IconSpinner from '../../assets/icons/spinner.svg?react';
import IconComment from '../../assets/icons/comment.svg?react';
import {getFlagUrlFromIso2} from '../../utils/flagUrl.ts';
import {formatNumber} from '../../utils/formatNumber.ts';
import {formatDate} from '../../utils/formatDate.ts';
import {initialsOf} from '../../utils/initials.ts';

interface AgencyStatsSectionProps {
    agencyId: string
    stats?: B2BAgencyStatsResponse
    isLoading: boolean
    isSelfService?: boolean
    onViewAllTripbooks?: () => void
    onViewReferrals?: () => void
}

const FUNNEL_STEP_COLOR: Record<string, string> = {
    contracted: 'bg-neutralBlack-900',
    tripCreated: 'bg-[#e8a200]',
    printed: 'bg-success',
    delivered: 'bg-[#04a3b3]',
}

export const AgencyStatsSection: React.FC<AgencyStatsSectionProps> = ({agencyId, stats, isLoading, isSelfService, onViewAllTripbooks, onViewReferrals}) => {
    const {t} = useTranslation('dashboard')
    const {feedbacks, isLoadingFeedbacks} = usePackagedAgencyFeedback(agencyId, isSelfService)
    const {stats: referralStats, isLoadingStats: isLoadingReferralStats} = useB2BReferralStats(agencyId, undefined, isSelfService)
    // The "Packs y TripBooks" KPI cards live here instead, for the tourOperator
    // staff view only — self-service agencies still see them on their own
    // "Packs y TripBooks" tab, so this stays disabled (and unfetched) for them.
    const {packs, isLoading: isLoadingPackActivity, isPayPerUse: isPackActivityPayPerUse, activity: packActivity} =
        useAgencyPackActivity(agencyId, undefined, isSelfService, !isSelfService)

    if (isLoading || !stats) {
        return (
            <div className="flex items-center justify-center py-16">
                <IconSpinner className="size-8"/>
            </div>
        )
    }

    const {cards, funnel} = stats
    const contracted = funnel.steps.find((step) => step.key === 'contracted')?.value ?? 0
    const isPayPerUse = contracted === 0

    if (isPayPerUse) {
        return (
            <PayPerUseStatsSection
                agencyId={agencyId}
                stats={stats}
                isSelfService={isSelfService}
                referralStats={referralStats}
                isLoadingReferralStats={isLoadingReferralStats}
                onViewReferrals={onViewReferrals}
            />
        )
    }

    const maxFunnelValue = funnel.steps[0]?.value ?? 0

    return (
        <div className="flex flex-col gap-3 pb-10">

            {isSelfService ? (
                <div className="flex gap-2.5 flex-wrap">
                    <StatCard
                        label={t('packs.statsTitleDeliverable')}
                        value={formatNumber(cards.unused.value)}
                        note={t('packs.statsNoteOfContracted', {n: formatNumber(cards.unused.contracted)})}
                    />
                    <StatCard
                        label={t('packs.statsActivationPct')}
                        value={formatPct(cards.activationPct.value)}
                        progress={cards.activationPct.value}
                        progressColor="bg-[#e8a200]"
                        note={t('packs.statsNoteNOfNContracted', {a: formatNumber(cards.activationPct.tripsCreated), b: formatNumber(cards.activationPct.contracted)})}
                    />
                    <StatCard
                        label={t('packs.statsPrintingPct')}
                        value={formatPct(cards.printingPct.value)}
                        progress={cards.printingPct.value}
                        progressColor="bg-success"
                        note={t('packs.statsNoteNOfNContracted', {a: formatNumber(cards.printingPct.printed), b: formatNumber(cards.printingPct.contracted)})}
                    />
                </div>
            ) : isLoadingPackActivity ? (
                <div className="flex items-center justify-center py-6">
                    <IconSpinner className="size-6"/>
                </div>
            ) : !isPackActivityPayPerUse && (
                <div className="flex gap-2.5 flex-wrap">
                    <StatCard
                        label={t('packs.kpiContracted')}
                        value={formatNumber(packActivity.totalUnits)}
                        note={packs.length === 1 ? t('packs.kpiContractedNoteOnePack') : t('packs.kpiContractedNoteManyPacks', {n: formatNumber(packs.length)})}
                    />
                    <StatCard
                        label={t('packs.kpiDelivered')}
                        value={`${packActivity.deliveredPct}%`}
                        progress={packActivity.deliveredPct / 100}
                        progressColor="bg-[#04A3B3]"
                        note={t('packs.kpiDeliveredNote', {n: formatNumber(packActivity.deliveredN)})}
                    />
                    <StatCard
                        label={t('packs.kpiCreated')}
                        value={`${packActivity.createdPct}%`}
                        progress={packActivity.createdPct / 100}
                        progressColor="bg-warning"
                        note={t('packs.kpiCreatedNote', {n: formatNumber(packActivity.createdN)})}
                    />
                    <StatCard
                        label={t('packs.kpiPrinted')}
                        value={formatNumber(packActivity.printedN)}
                        note={t('packs.kpiPrintedNote')}
                    />
                </div>
            )}

            <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm p-5 flex flex-col gap-4">
                <div className="flex items-baseline gap-2.5 flex-wrap">
                    <span className="text-[15px] font-semibold tracking-tight text-neutralBlack-900">{t('packs.statsJourneyTitle')}</span>
                    <span className="text-[13px] text-neutralBlack-500">{t('packs.statsJourneySubtitle')}</span>
                </div>

                <div className="flex flex-col gap-3.5">
                    {funnel.steps.map((step) => (
                        <div key={step.key} className="flex items-center gap-3.5">
                            <span className="w-[104px] max-[860px]:w-[86px] flex-none text-[13px] max-[860px]:text-xs text-neutralBlack-800">{step.label}</span>
                            <span className="flex-1 min-w-0 h-[34px] rounded-[10px] bg-neutralBlack-50 overflow-hidden flex items-center">
                                <span
                                    className={`h-[34px] rounded-[10px] ${FUNNEL_STEP_COLOR[step.key] ?? 'bg-neutralBlack-900'}`}
                                    style={{width: `${maxFunnelValue ? Math.min((step.value / maxFunnelValue) * 100, 100) : 0}%`}}
                                />
                            </span>
                            <span className="w-14 flex-none text-right text-base font-semibold text-neutralBlack-900">{formatNumber(step.value)}</span>
                            <span className="w-12 flex-none text-right text-xs text-neutralBlack-500">
                                {step.delta != null && step.delta !== 0 && (step.delta > 0 ? `+${formatNumber(step.delta)}` : formatNumber(step.delta))}
                            </span>
                        </div>
                    ))}
                </div>


            </section>

            <ClientTripbooksSection tripbooks={stats.clientTripbooks} onViewAll={onViewAllTripbooks}/>

            <div className="flex gap-3 items-stretch flex-wrap">
                <ClientFeedbackSection feedbacks={feedbacks} isLoading={isLoadingFeedbacks}/>
                <div className="flex-1 min-w-[300px] flex flex-col gap-3">
                    <MonthlyEvolutionSection/>
                    <ReferralSummaryCard stats={referralStats} isLoading={isLoadingReferralStats} onViewReferrals={onViewReferrals}/>
                </div>
            </div>
        </div>
    )
}

const PayPerUseStatsSection: React.FC<{
    agencyId: string
    stats: B2BAgencyStatsResponse
    isSelfService?: boolean
    referralStats?: B2BReferralStatsResponse
    isLoadingReferralStats: boolean
    onViewReferrals?: () => void
}> = ({agencyId, stats, isSelfService, referralStats, isLoadingReferralStats, onViewReferrals}) => {
    const {t} = useTranslation('dashboard')
    const {funnel, reference} = stats
    const tripsCreated = funnel.steps.find((step) => step.key === 'tripCreated')?.value ?? 0
    const printed = funnel.steps.find((step) => step.key === 'printed')?.value ?? 0
    const {tripbooks, isLoading, isFetchingNextPage, hasNextPage, loadMore} = useAgencyTripbooksFeed(agencyId, true, isSelfService)
    const {feedbacks, isLoadingFeedbacks} = usePayPerUseAgencyFeedback(agencyId, isSelfService)

    return (
        <div className="flex flex-col gap-3 pb-10">

            <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 bg-neutralBlack-100 rounded-full px-2.5 py-1 text-xs text-neutralBlack-900">
                    {t('packs.statsPayPerUseBadge')}
                </span>
                <span className="text-[13px] text-neutralBlack-500">{t('packs.statsPayPerUseHelp')}</span>
            </div>

            <div className="flex gap-2.5 flex-wrap">
                <StatCard label={t('packs.statsTripsCreated')} value={formatNumber(tripsCreated)} note={t('packs.statsTripsCreatedNote')}/>
                <StatCard label={t('packs.kpiPrinted')} value={formatNumber(printed)} note={t('packs.statsPrintedUnitsNote')}/>
                <StatCard label={t('packs.statsPrintOrders')} value={formatNumber(reference.printOrders)} note={t('packs.statsPrintedCopiesNote', {n: formatNumber(reference.printedCopies)})}/>
            </div>

            <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm p-5 flex flex-col gap-4">
                <span className="text-[15px] font-semibold tracking-tight text-neutralBlack-900">{t('packs.statsClientTripbooksTitle')}</span>
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <IconSpinner className="size-6"/>
                    </div>
                ) : tripbooks.length === 0 ? (
                    <div className="text-center py-8 text-sm text-neutralBlack-500">{t('packs.statsNoTripbooksYet')}</div>
                ) : (
                    <>
                        <ClientTripbooksList tripbooks={tripbooks}/>
                        {hasNextPage && (
                            <button
                                type="button"
                                onClick={() => loadMore()}
                                disabled={isFetchingNextPage}
                                className="self-center text-[13px] font-medium text-neutralBlack-700 hover:text-neutralBlack-900 disabled:opacity-60 py-1.5"
                            >
                                {isFetchingNextPage ? t('loading') : t('packs.statsLoadMore')}
                            </button>
                        )}
                    </>
                )}
            </section>

            <div className="flex gap-3 items-stretch flex-wrap">
                <ClientFeedbackSection feedbacks={feedbacks} isLoading={isLoadingFeedbacks}/>
                <div className="flex-1 min-w-[300px] flex flex-col gap-3">
                    <MonthlyEvolutionSection/>
                    <ReferralSummaryCard stats={referralStats} isLoading={isLoadingReferralStats} onViewReferrals={onViewReferrals}/>
                </div>
            </div>
        </div>
    )
}

const ClientTripbooksSection: React.FC<{tripbooks: ClientTripbook[]; onViewAll?: () => void}> = ({tripbooks, onViewAll}) => {
    const {t} = useTranslation('dashboard')
    return (
        <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm p-5 flex flex-col gap-4">
            <div className="flex items-baseline gap-2.5 flex-wrap">
                <span className="text-[15px] font-semibold tracking-tight text-neutralBlack-900">{t('packs.statsClientTripbooksTitle')}</span>
                <span className="flex-1 min-w-1.5"/>
                {onViewAll && tripbooks.length > 0 && (
                    <button type="button" onClick={onViewAll} className="text-[13px] whitespace-nowrap text-[#3E63DD] hover:underline">
                        {t('packs.statsViewAll')}
                    </button>
                )}
            </div>
            {tripbooks.length === 0 ? (
                <div className="text-center py-8 text-sm text-neutralBlack-500">{t('packs.statsNoTripbooksYet')}</div>
            ) : (
                <ClientTripbooksList tripbooks={tripbooks}/>
            )}
        </section>
    )
}

const ClientFeedbackSection: React.FC<{feedbacks: AgencyFeedbackEntry[]; isLoading: boolean}> = ({feedbacks, isLoading}) => {
    const {t} = useTranslation('dashboard')
    const visible = feedbacks.slice(0, 5)
    return (
        <section className="flex-1 min-w-[300px] bg-white border border-neutralBlack-200 rounded-2xl shadow-sm p-5 flex flex-col gap-4">
            <div className="flex items-baseline gap-2.5 flex-wrap">
                <span className="text-[15px] font-semibold tracking-tight text-neutralBlack-900">{t('packs.statsFeedbackTitle')}</span>
                <span className="flex-1 min-w-1.5"/>
                <span className="text-[22px] font-semibold text-neutralBlack-900">{formatNumber(feedbacks.length)}</span>
                <span className="text-xs text-neutralBlack-500">{t('packs.statsFeedbackCountLabel')}</span>
            </div>
            {isLoading ? (
                <div className="flex items-center justify-center py-8">
                    <IconSpinner className="size-6"/>
                </div>
            ) : visible.length === 0 ? (
                <div className="flex flex-col items-center text-center gap-2.5 py-6 px-2">
                    <span className="w-[52px] h-[52px] rounded-full bg-neutralBlack-50 flex items-center justify-center text-neutralBlack-400">
                        <IconComment className="size-[22px]"/>
                    </span>
                    <span className="text-sm font-medium text-neutralBlack-900">{t('packs.statsFeedbackEmptyTitle')}</span>
                    <span className="max-w-[260px] text-xs leading-relaxed text-neutralBlack-500">{t('packs.statsFeedbackEmptyHelp')}</span>
                </div>
            ) : (
                <div className="flex flex-col gap-3.5">
                    {visible.map((f) => (
                        <div key={f.id} className="flex gap-3 pb-3.5 border-b border-neutralBlack-100 last:border-0 last:pb-0">
                            <span className="w-8 h-8 flex-none rounded-full bg-neutralBlack-100 flex items-center justify-center text-[11.5px] font-semibold text-neutralBlack-700">
                                {initialsOf(f.traveler.name ?? undefined)}
                            </span>
                            <span className="flex-1 min-w-0 flex flex-col gap-1">
                                <span className="flex items-baseline gap-2 flex-wrap">
                                    <span className="text-[13.5px] font-medium text-neutralBlack-900">{f.traveler.name ?? '—'}</span>
                                    <span className="text-[11.5px] text-neutralBlack-500">{formatDate(f.feedback.feedbackDate)}</span>
                                </span>
                                <span className="text-[13.5px] leading-relaxed text-neutralBlack-800">{f.feedback.comment}</span>
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </section>
    )
}

// The design's "Evolución mensual" (Creados/Impresos por mes) chart needs a
// per-agency monthly time series that doesn't exist in any B2B metrics
// endpoint yet — B2BAgencyStatsResponse only has cumulative totals. The shell
// is wired up pixel-for-pixel so it only needs real series data plugged in
// once the backend exposes it.
const MonthlyEvolutionSection: React.FC = () => {
    const {t} = useTranslation('dashboard')
    return (
        <section className="flex-1 bg-white border border-neutralBlack-200 rounded-2xl shadow-sm p-5 flex flex-col gap-[18px]">
            <div className="flex items-center gap-4 flex-wrap">
                <span className="text-[15px] font-semibold tracking-tight text-neutralBlack-900">{t('packs.statsMonthlyEvolutionTitle')}</span>
                <span className="flex-1 min-w-1.5"/>
                <span className="inline-flex items-center gap-1.5 text-xs text-neutralBlack-800">
                    <span className="w-[9px] h-[9px] rounded-[3px]" style={{background: '#E8A200'}}/>
                    {t('metrics.legendCreated')}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs text-neutralBlack-800">
                    <span className="w-[9px] h-[9px] rounded-[3px]" style={{background: '#18AE60'}}/>
                    {t('metrics.legendPrinted')}
                </span>
            </div>
            <div className="h-[150px] flex items-center justify-center">
                <span className="max-w-[220px] text-center text-xs text-neutralBlack-400">{t('packs.statsMonthlyEvolutionEmpty')}</span>
            </div>
        </section>
    )
}

const ReferralSummaryCard: React.FC<{stats?: B2BReferralStatsResponse; isLoading: boolean; onViewReferrals?: () => void}> = ({stats, isLoading, onViewReferrals}) => {
    const {t} = useTranslation('dashboard')
    const hasReferrals = !isLoading && !!stats && stats.cards.referrals > 0

    return (
        <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm p-5 flex items-center gap-4 flex-wrap">
            <span className="flex-1 min-w-[170px] flex flex-col gap-1">
                <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('referrals.referrals')}</span>
                {isLoading ? (
                    <IconSpinner className="size-5 mt-1"/>
                ) : hasReferrals && stats ? (
                    <>
                        <span className="flex items-baseline gap-2.5 flex-wrap">
                            <span className="text-[22px] font-semibold tracking-tight text-neutralBlack-900">
                                {formatMoney(stats.cards.generatedVolume.cash, stats.cards.generatedVolume.currency)}
                            </span>
                            {stats.cards.generatedVolume.tripbooks != null && (
                                <span className="text-[13px] text-neutralBlack-500">
                                    {t('referrals.or')} {t('referrals.tripbooksSuffix', {n: stats.cards.generatedVolume.tripbooks})}
                                </span>
                            )}
                        </span>
                        <span className="text-xs text-neutralBlack-500">{t('packs.statsReferralsNote')}</span>
                    </>
                ) : (
                    <>
                        <span className="text-[15px] font-medium text-neutralBlack-900">{t('packs.statsReferralsEmptyTitle')}</span>
                        <span className="text-xs leading-relaxed text-neutralBlack-500">{t('packs.statsReferralsEmptyHelp')}</span>
                    </>
                )}
            </span>
            {onViewReferrals && (
                <Button variant="secondary" size="small" className="max-h-[40px]" onClick={onViewReferrals}>
                    {t('packs.statsViewReferrals')}
                </Button>
            )}
        </section>
    )
}

function formatMoney(amount: number, currency: string): string {
    return new Intl.NumberFormat('es-ES', {style: 'currency', currency, maximumFractionDigits: 0, useGrouping: 'always'} as any).format(amount)
}

const ClientTripbooksList: React.FC<{tripbooks: ClientTripbook[]}> = ({tripbooks}) => {
    console.log('ClientTripbooksList', tripbooks)
    const {t} = useTranslation('dashboard')
    return (
    <div className="flex flex-col">
        {tripbooks.map((tb) => (
            <div key={tb.tripbookId} className="flex items-center gap-3.5 py-3 border-b border-neutralBlack-100 last:border-0 flex-wrap">
                <span className="w-[34px] h-[34px] flex-none rounded-[10px] bg-neutralBlack-100 flex items-center justify-center text-neutralBlack-800">
                    <IconBook className="w-[17px] h-[17px]"/>
                </span>
                <span className="flex-1 min-w-[150px] flex flex-col gap-0.5">
                    <span className="text-[13.5px] font-medium text-neutralBlack-900">{tb.client}</span>
                    <span className="flex items-center gap-1.5 text-xs text-neutralBlack-500 flex-wrap">
                        {tb.countries.map((c) => (
                            <img
                                key={c.iso2}
                                src={getFlagUrlFromIso2(c.iso2)}
                                alt={c.name}
                                className="w-3.5 h-2.5 rounded-sm object-cover shadow-[0_0_0_1px_rgba(0,0,0,.08)]"
                            />
                        ))}
                        {tb.countries.map(c => c.name).join(' · ')}
                        {tb.tripMonth && ` · ${formatMonthYear(tb.tripMonth)}`}
                    </span>
                </span>
                {tb.url && (
                    <a href={tb.url} target="_blank" rel="noreferrer" className="text-[13px] whitespace-nowrap text-[#3E63DD] hover:underline">
                        {t('packs.viewTripbook')}
                    </a>
                )}
            </div>
        ))}
    </div>
    )
}

const StatCard: React.FC<{
    label: string
    value: string
    note?: string
    progress?: number
    progressColor?: string
}> = ({label, value, note, progress, progressColor}) => (
    <div className="flex-1 min-w-[170px] bg-white border border-neutralBlack-200 rounded-2xl shadow-sm px-[18px] py-4 flex flex-col gap-1.5">
        <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{label}</span>
        {progress !== undefined ? (
            <span className="flex items-center gap-2.5">
                <span className="text-[22px] font-semibold tracking-tight text-neutralBlack-900">{value}</span>
                <span className="flex-1 max-w-[90px] h-1 rounded-full bg-neutralBlack-200 overflow-hidden">
                    <span
                        className={`block h-1 rounded-full ${progressColor}`}
                        style={{width: `${Math.min(progress * 100, 100)}%`}}
                    />
                </span>
            </span>
        ) : (
            <span className="text-[22px] font-semibold tracking-tight text-neutralBlack-900">{value}</span>
        )}
        {note && <span className="text-xs text-neutralBlack-500">{note}</span>}
    </div>
)

function formatPct(value: number): string {
    return `${Math.round(value * 100)}%`
}

function formatMonthYear(monthStr: string): string {
    const [year, month] = monthStr.split('-').map(Number)
    if (!year || !month) return monthStr
    return new Intl.DateTimeFormat('es-ES', {month: 'short', year: 'numeric'}).format(new Date(year, month - 1, 1))
}
